import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';

/**
 * @description Auth session payload persisted in localStorage and broadcast
 * across tabs.
 */
export interface IAuthSession {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAt: string;
  refreshTokenExpiresAt: string;
}

const TOKEN_KEY = 'authToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const ACCESS_EXPIRES_AT_KEY = 'accessTokenExpiresAt';
const REFRESH_EXPIRES_AT_KEY = 'refreshTokenExpiresAt';
const BROADCAST_CHANNEL = 'auth-session';

interface IBroadcastMessage {
  kind: 'set' | 'clear';
  session?: IAuthSession;
}

/**
 * Service for managing authentication tokens in localStorage.
 *
 * MVP-33: persists access + refresh + both expiries, and synchronises across
 * browser tabs via BroadcastChannel (storage-event fallback) so a token
 * rotation in one tab does not silently log out the others.
 */
@Injectable({
  providedIn: 'root',
})
export class TokenStorageService implements OnDestroy {
  /** Emits whenever another tab updates or clears the auth session. */
  readonly remoteChange$ = new Subject<IAuthSession | null>();

  private readonly broadcastChannel: BroadcastChannel | null = null;
  private readonly storageHandler: ((event: StorageEvent) => void) | null = null;

  constructor() {
    if (typeof window === 'undefined') {
      return;
    }

    if (typeof BroadcastChannel !== 'undefined') {
      this.broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL);
      this.broadcastChannel.onmessage = (event: MessageEvent<IBroadcastMessage>) => {
        this.applyRemoteChange(event.data);
      };
    } else {
      this.storageHandler = (event: StorageEvent) => {
        if (
          event.key === TOKEN_KEY ||
          event.key === REFRESH_TOKEN_KEY ||
          event.key === ACCESS_EXPIRES_AT_KEY ||
          event.key === REFRESH_EXPIRES_AT_KEY
        ) {
          this.remoteChange$.next(this.readSession());
        }
      };
      window.addEventListener('storage', this.storageHandler);
    }
  }

  ngOnDestroy(): void {
    this.broadcastChannel?.close();
    if (this.storageHandler) {
      window.removeEventListener('storage', this.storageHandler);
    }
    this.remoteChange$.complete();
  }

  /**
   * @description Persists a full auth session and broadcasts to other tabs.
   */
  setAuthSession(session: IAuthSession): void {
    localStorage.setItem(TOKEN_KEY, session.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, session.refreshToken);
    localStorage.setItem(ACCESS_EXPIRES_AT_KEY, session.accessTokenExpiresAt);
    localStorage.setItem(REFRESH_EXPIRES_AT_KEY, session.refreshTokenExpiresAt);
    this.broadcast({ kind: 'set', session });
  }

  /**
   * @description Stores only the access token. Kept for backwards compatibility
   * with code paths that have not yet been migrated to the full session shape.
   */
  setToken(token: string): void {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    }
  }

  /**
   * @description Retrieves the access token from localStorage.
   */
  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  /**
   * @description Retrieves the refresh token from localStorage.
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  /**
   * @description Retrieves the access-token expiry timestamp (ISO string).
   */
  getAccessTokenExpiresAt(): string | null {
    return localStorage.getItem(ACCESS_EXPIRES_AT_KEY);
  }

  /**
   * @description Retrieves the refresh-token expiry timestamp (ISO string).
   */
  getRefreshTokenExpiresAt(): string | null {
    return localStorage.getItem(REFRESH_EXPIRES_AT_KEY);
  }

  /**
   * @description Clears the access token only. Delegates to clearAll so
   * legacy callers do not leave a half-cleared session behind.
   */
  clearToken(): void {
    this.clearAll();
  }

  /**
   * @description Removes all auth-session keys and broadcasts to other tabs.
   */
  clearAll(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(ACCESS_EXPIRES_AT_KEY);
    localStorage.removeItem(REFRESH_EXPIRES_AT_KEY);
    this.broadcast({ kind: 'clear' });
  }

  /**
   * @description Checks if an access token exists in localStorage.
   */
  hasToken(): boolean {
    return !!this.getToken();
  }

  private readSession(): IAuthSession | null {
    const accessToken = this.getToken();
    const refreshToken = this.getRefreshToken();
    const accessTokenExpiresAt = this.getAccessTokenExpiresAt();
    const refreshTokenExpiresAt = this.getRefreshTokenExpiresAt();
    if (!accessToken || !refreshToken || !accessTokenExpiresAt || !refreshTokenExpiresAt) {
      return null;
    }
    return { accessToken, refreshToken, accessTokenExpiresAt, refreshTokenExpiresAt };
  }

  private broadcast(message: IBroadcastMessage): void {
    this.broadcastChannel?.postMessage(message);
  }

  private applyRemoteChange(message: IBroadcastMessage | undefined): void {
    if (!message) return;
    if (message.kind === 'set' && message.session) {
      // Mirror the rotated tokens into this tab's storage without rebroadcasting.
      localStorage.setItem(TOKEN_KEY, message.session.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, message.session.refreshToken);
      localStorage.setItem(ACCESS_EXPIRES_AT_KEY, message.session.accessTokenExpiresAt);
      localStorage.setItem(REFRESH_EXPIRES_AT_KEY, message.session.refreshTokenExpiresAt);
      this.remoteChange$.next(message.session);
      return;
    }
    if (message.kind === 'clear') {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(ACCESS_EXPIRES_AT_KEY);
      localStorage.removeItem(REFRESH_EXPIRES_AT_KEY);
      this.remoteChange$.next(null);
    }
  }
}
